import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Card } from "./ui/card";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { createSuggestion, updateSuggestion, deleteSuggestion, getSuggestions } from '../api/improvementSuggestionsApi';

const PRIORITY_LEVELS = [
  { value: 0, label: 'Low' },
  { value: 1, label: 'Medium' },
  { value: 2, label: 'High' },
  { value: 3, label: 'Critical' }
];

const DIFFICULTY_LEVELS = [
  { value: 0, label: 'Easy' },
  { value: 1, label: 'Medium' },
  { value: 2, label: 'Hard' }
];

const ImprovementSuggestions = ({ accessCode, contributorId, factorId }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newSuggestion, setNewSuggestion] = useState({
    suggestion: '',
    priority: '0',
    implementation_difficulty: '1',
    facilitator_notes: ''
  });

  useEffect(() => {
    if (accessCode && contributorId && factorId) {
      loadSuggestions();
    }
  }, [accessCode, contributorId, factorId]);

  const loadSuggestions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getSuggestions(accessCode, { factorId, contributorId });
      console.log('API Response:', response); // Debug log
      
      // Check if response has the expected structure
      const suggestionsList = response?.suggestions || [];
      setSuggestions(suggestionsList);
    } catch (err) {
      console.error('Error loading suggestions:', err);
      setError('Failed to load suggestions');
      setSuggestions([]); // Reset to empty array on error
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setError(null);
      const response = await createSuggestion(accessCode, {
        ...newSuggestion,
        contributor_id: contributorId,
        factor_id: factorId
      });
      console.log('Create Response:', response); // Debug log
      
      setNewSuggestion({
        suggestion: '',
        priority: '0',
        implementation_difficulty: '1',
        facilitator_notes: ''
      });
      loadSuggestions();
    } catch (err) {
      console.error('Error creating suggestion:', err);
      setError('Failed to create suggestion');
    }
  };

  const handleDelete = async (id) => {
    try {
      setError(null);
      await deleteSuggestion(accessCode, id);
      loadSuggestions();
    } catch (err) {
      console.error('Error deleting suggestion:', err);
      setError('Failed to delete suggestion');
    }
  };

  if (!accessCode || !contributorId || !factorId) {
    return <div>Missing required props</div>;
  }

  if (loading) {
    return <div className="text-center py-4">Loading suggestions...</div>;
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-destructive-50 text-destructive p-4 rounded-md mb-4">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1">Suggestion</label>
          <Textarea
            value={newSuggestion.suggestion}
            onChange={(e) => setNewSuggestion(prev => ({ ...prev, suggestion: e.target.value }))}
            required
            placeholder="Enter your suggestion here..."
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Priority</label>
            <Select
              value={newSuggestion.priority}
              onValueChange={(value) => setNewSuggestion(prev => ({ ...prev, priority: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">Low</SelectItem>
                <SelectItem value="1">Medium</SelectItem>
                <SelectItem value="2">High</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Implementation Difficulty</label>
            <Select
              value={newSuggestion.implementation_difficulty}
              onValueChange={(value) => setNewSuggestion(prev => ({ ...prev, implementation_difficulty: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select difficulty" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Easy</SelectItem>
                <SelectItem value="2">Medium</SelectItem>
                <SelectItem value="3">Hard</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Facilitator Notes</label>
          <Textarea
            value={newSuggestion.facilitator_notes}
            onChange={(e) => setNewSuggestion(prev => ({ ...prev, facilitator_notes: e.target.value }))}
            placeholder="Add any additional notes..."
          />
        </div>

        <Button type="submit">Add Suggestion</Button>
      </form>

      {/* Divider and Existing Suggestions Header */}
        <div className="relative my-8">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-border"></div>
        </div>
        <div className="relative flex justify-between items-center">
          <span className="bg-card px-3 py-1 text-lg font-semibold text-foreground">
            Suggestions {suggestions.length > 0 && `(${suggestions.length})`}
          </span>
          {suggestions.length > 3 && (
            <span className="bg-card px-3 text-sm text-muted-foreground">
              Showing all {suggestions.length} suggestions
            </span>
          )}
        </div>
      </div>

      <div className="space-y-4">
        {Array.isArray(suggestions) && suggestions.length > 0 ? (
          suggestions.map((suggestion, index) => (
            <Card 
              key={suggestion.id} 
              className={`p-4 border-l-4 hover:shadow-md transition-shadow duration-200 ${
                suggestion.priority === 2 ? 'border-l-destructive' :
                suggestion.priority === 1 ? 'border-l-warning' :
                'border-l-success'
              }`}
            >
              <div className="flex justify-between items-start">
                <div className="space-y-2 flex-1">
                  <div className="flex justify-between">
                    <p className="font-medium text-foreground">{suggestion.suggestion}</p>
                    <Badge variant="default" tint className="ml-2">#{index + 1}</Badge>
                  </div>
                  {suggestion.facilitator_notes && (
                    <div className="bg-muted p-2 rounded border-l-2 border-border mt-2">
                      <p className="text-sm text-muted-foreground italic">{suggestion.facilitator_notes}</p>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2 mt-2">
                    <Badge
                      tint
                      variant={
                        suggestion.priority === 2 ? 'destructive' :
                        suggestion.priority === 1 ? 'warning' :
                        'success'
                      }
                    >
                      Priority: {PRIORITY_LEVELS[suggestion.priority]?.label || 'Low'}
                    </Badge>
                    <Badge
                      tint
                      variant={
                        suggestion.implementation_difficulty === 3 ? 'destructive' :
                        suggestion.implementation_difficulty === 2 ? 'warning' :
                        'success'
                      }
                    >
                      Difficulty: {DIFFICULTY_LEVELS[suggestion.implementation_difficulty - 1]?.label || 'Easy'}
                    </Badge>
                  </div>
                </div>
                <Button 
                  variant="destructive" 
                  onClick={() => handleDelete(suggestion.id)}
                  className="ml-4 self-start"
                  size="sm"
                >
                  Delete
                </Button>
              </div>
            </Card>
          ))
        ) : (
          <div className="text-center text-muted-foreground py-8 bg-muted rounded-lg border border-dashed border-border">
            <p className="font-medium">No suggestions yet</p>
            <p className="text-sm mt-1">Add your first suggestion using the form above</p>
          </div>
        )}
      </div>
    </div>
  );
};

ImprovementSuggestions.propTypes = {
  accessCode: PropTypes.string.isRequired,
  contributorId: PropTypes.string.isRequired,
  factorId: PropTypes.string.isRequired
};

export default ImprovementSuggestions; 